import type { ThemeTokens, ThemePreset, ThemeCategoryConfig } from '../types/theme';

export const DEFAULT_LIGHT_TOKENS: ThemeTokens = {
  // Shell
  appBackground: '#F8FAFC',
  primarySidebarBackground: '#FFFFFF',
  miniSidebarBackground: '#F1F5F9',
  headerBackground: '#FFFFFF',
  sidebarText: '#0F172A',
  sidebarSecondaryText: '#64748B',
  sidebarActiveBackground: '#EEF2FF',
  sidebarActiveText: '#4F46E5',
  sidebarHoverBackground: '#F8FAFC',
  sidebarBorder: '#E2E8F0',

  // Chat
  chatBackground: '#F8FAFC',
  messageText: '#0F172A',
  messageSecondaryText: '#64748B',
  messageHoverBackground: '#F1F5F9',
  incomingMessageBackground: '#FFFFFF',
  outgoingMessageBackground: '#EEF2FF',
  messageTimestamp: '#94A3B8',
  threadBackground: '#FFFFFF',
  composerBackground: '#FFFFFF',
  composerBorder: '#E2E8F0',
  composerText: '#0F172A',
  composerPlaceholder: '#94A3B8',

  // Interactive
  primaryAccent: '#4F46E5',
  primaryButtonBackground: '#4F46E5',
  primaryButtonText: '#FFFFFF',
  primaryButtonHover: '#4338CA',
  secondaryButtonBackground: '#F1F5F9',
  secondaryButtonText: '#0F172A',
  secondaryButtonHover: '#E2E8F0',
  iconColor: '#64748B',
  iconHoverColor: '#0F172A',
  linkColor: '#4F46E5',
  focusRing: '#4F46E5',
  selectedState: '#EEF2FF',
  activeState: '#E0E7FF',

  // Mentions & Reactions
  mentionBackground: '#EEF2FF',
  mentionText: '#4F46E5',
  mentionHoverBackground: '#E0E7FF',
  reactionBackground: '#F1F5F9',
  reactionActiveBackground: '#EEF2FF',
  reactionActiveBorder: '#818CF8',
  reactionBorder: '#E2E8F0',

  // Cards & Overlays
  cardBackground: '#FFFFFF',
  modalBackground: '#FFFFFF',
  dropdownBackground: '#FFFFFF',
  popoverBackground: '#FFFFFF',
  tooltipBackground: '#0F172A',
  tooltipText: '#F8FAFC',
  borderColor: '#E2E8F0',
  dividerColor: '#E2E8F0',
  inputBackground: '#FFFFFF',
  inputBorder: '#CBD5E1',
  inputFocusBorder: '#4F46E5',

  // Status
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6',
  statusOnline: '#10B981',
  statusAway: '#F59E0B',
  statusOffline: '#94A3B8',

  // Code
  codeBlockBackground: '#0F172A',
  codeBlockText: '#F8FAFC',
  inlineCodeBackground: '#F1F5F9',
  inlineCodeText: '#4F46E5',
  syntaxKeyword: '#EC4899',
  syntaxString: '#10B981',
  syntaxFunction: '#3B82F6',
  syntaxComment: '#64748B',
};

export const DEFAULT_DARK_TOKENS: ThemeTokens = {
  // Shell
  appBackground: '#0B0F19',
  primarySidebarBackground: '#111827',
  miniSidebarBackground: '#0F172A',
  headerBackground: '#111827',
  sidebarText: '#F8FAFC',
  sidebarSecondaryText: '#94A3B8',
  sidebarActiveBackground: '#1E1B4B',
  sidebarActiveText: '#A5B4FC',
  sidebarHoverBackground: '#1F2937',
  sidebarBorder: '#1F2937',

  // Chat
  chatBackground: '#0B0F19',
  messageText: '#F8FAFC',
  messageSecondaryText: '#94A3B8',
  messageHoverBackground: '#1F2937',
  incomingMessageBackground: '#111827',
  outgoingMessageBackground: '#1E1B4B',
  messageTimestamp: '#64748B',
  threadBackground: '#111827',
  composerBackground: '#111827',
  composerBorder: '#374151',
  composerText: '#F8FAFC',
  composerPlaceholder: '#64748B',

  // Interactive
  primaryAccent: '#6366F1',
  primaryButtonBackground: '#6366F1',
  primaryButtonText: '#FFFFFF',
  primaryButtonHover: '#4F46E5',
  secondaryButtonBackground: '#1F2937',
  secondaryButtonText: '#F8FAFC',
  secondaryButtonHover: '#374151',
  iconColor: '#94A3B8',
  iconHoverColor: '#F8FAFC',
  linkColor: '#818CF8',
  focusRing: '#6366F1',
  selectedState: '#1E1B4B',
  activeState: '#312E81',

  // Mentions & Reactions
  mentionBackground: '#1E1B4B',
  mentionText: '#A5B4FC',
  mentionHoverBackground: '#312E81',
  reactionBackground: '#1F2937',
  reactionActiveBackground: '#1E1B4B',
  reactionActiveBorder: '#6366F1',
  reactionBorder: '#374151',

  // Cards & Overlays
  cardBackground: '#111827',
  modalBackground: '#111827',
  dropdownBackground: '#111827',
  popoverBackground: '#111827',
  tooltipBackground: '#020617',
  tooltipText: '#F8FAFC',
  borderColor: '#1F2937',
  dividerColor: '#1F2937',
  inputBackground: '#0B0F19',
  inputBorder: '#374151',
  inputFocusBorder: '#6366F1',

  // Status
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6',
  statusOnline: '#10B981',
  statusAway: '#F59E0B',
  statusOffline: '#64748B',

  // Code
  codeBlockBackground: '#030712',
  codeBlockText: '#E2E8F0',
  inlineCodeBackground: '#1F2937',
  inlineCodeText: '#A5B4FC',
  syntaxKeyword: '#F472B6',
  syntaxString: '#34D399',
  syntaxFunction: '#60A5FA',
  syntaxComment: '#6B7280',
};

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'defaultLight',
    name: 'Default Light',
    description: 'Clean, balanced SaaS light theme with crisp typography and subtle indigo accents',
    baseMode: 'light',
    tokens: DEFAULT_LIGHT_TOKENS,
    previewColors: {
      sidebar: '#FFFFFF',
      chat: '#F8FAFC',
      primary: '#4F46E5',
      card: '#FFFFFF',
    },
  },
  {
    id: 'defaultDark',
    name: 'Default Dark',
    description: 'Modern midnight slate dark mode designed for comfortable, low-glare productivity',
    baseMode: 'dark',
    tokens: DEFAULT_DARK_TOKENS,
    previewColors: {
      sidebar: '#111827',
      chat: '#0B0F19',
      primary: '#6366F1',
      card: '#111827',
    },
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'Corporate royal blue and silver slate tailored for enterprise team clarity',
    baseMode: 'light',
    tokens: {
      ...DEFAULT_LIGHT_TOKENS,
      appBackground: '#F1F5F9',
      primarySidebarBackground: '#0F172A',
      miniSidebarBackground: '#020617',
      headerBackground: '#FFFFFF',
      sidebarText: '#F8FAFC',
      sidebarSecondaryText: '#94A3B8',
      sidebarActiveBackground: '#1E293B',
      sidebarActiveText: '#38BDF8',
      sidebarHoverBackground: '#1E293B',
      sidebarBorder: '#1E293B',
      primaryAccent: '#0284C7',
      primaryButtonBackground: '#0284C7',
      primaryButtonHover: '#0369A1',
      linkColor: '#0284C7',
      focusRing: '#0284C7',
      mentionBackground: '#E0F2FE',
      mentionText: '#0369A1',
      mentionHoverBackground: '#BAE6FD',
      reactionActiveBackground: '#E0F2FE',
      reactionActiveBorder: '#38BDF8',
    },
    previewColors: {
      sidebar: '#0F172A',
      chat: '#F1F5F9',
      primary: '#0284C7',
      card: '#FFFFFF',
    },
  },
  {
    id: 'midnight',
    name: 'Midnight Indigo',
    description: 'Deep #1a1b4b midnight navy rail with radiant electric violet and neon cyan accents',
    baseMode: 'dark',
    tokens: {
      ...DEFAULT_DARK_TOKENS,
      appBackground: '#0A0B1A',
      primarySidebarBackground: '#12132D',
      miniSidebarBackground: '#1a1b4b',
      headerBackground: '#12132D',
      sidebarText: '#EEF2FF',
      sidebarSecondaryText: '#818CF8',
      sidebarActiveBackground: '#2E286E',
      sidebarActiveText: '#C7D2FE',
      sidebarHoverBackground: '#1E1F42',
      sidebarBorder: '#1E204D',
      chatBackground: '#0A0B1A',
      incomingMessageBackground: '#12132D',
      outgoingMessageBackground: '#241E5C',
      composerBackground: '#12132D',
      composerBorder: '#2E3275',
      primaryAccent: '#7C3AED',
      primaryButtonBackground: '#7C3AED',
      primaryButtonHover: '#6D28D9',
      linkColor: '#A78BFA',
      focusRing: '#7C3AED',
      mentionBackground: '#2E286E',
      mentionText: '#DDD6FE',
      mentionHoverBackground: '#4C1D95',
      cardBackground: '#12132D',
      modalBackground: '#12132D',
      dropdownBackground: '#12132D',
      popoverBackground: '#12132D',
      borderColor: '#1E204D',
    },
    previewColors: {
      sidebar: '#1a1b4b',
      chat: '#0A0B1A',
      primary: '#7C3AED',
      card: '#12132D',
    },
  },
  {
    id: 'forest',
    name: 'Forest Nature',
    description: 'Earthy evergreen, sage green and rich dark pine inspired by natural ecosystems',
    baseMode: 'dark',
    tokens: {
      ...DEFAULT_DARK_TOKENS,
      appBackground: '#06130D',
      primarySidebarBackground: '#0A1C14',
      miniSidebarBackground: '#040F0A',
      headerBackground: '#0A1C14',
      sidebarText: '#ECFDF5',
      sidebarSecondaryText: '#6EE7B7',
      sidebarActiveBackground: '#133527',
      sidebarActiveText: '#34D399',
      sidebarHoverBackground: '#0F291D',
      sidebarBorder: '#133527',
      chatBackground: '#06130D',
      incomingMessageBackground: '#0A1C14',
      outgoingMessageBackground: '#133527',
      composerBackground: '#0A1C14',
      composerBorder: '#1A4D38',
      primaryAccent: '#10B981',
      primaryButtonBackground: '#10B981',
      primaryButtonHover: '#059669',
      linkColor: '#34D399',
      focusRing: '#10B981',
      mentionBackground: '#133527',
      mentionText: '#6EE7B7',
      mentionHoverBackground: '#064E3B',
      cardBackground: '#0A1C14',
      modalBackground: '#0A1C14',
      dropdownBackground: '#0A1C14',
      popoverBackground: '#0A1C14',
      borderColor: '#133527',
    },
    previewColors: {
      sidebar: '#0A1C14',
      chat: '#06130D',
      primary: '#10B981',
      card: '#0A1C14',
    },
  },
  {
    id: 'ocean',
    name: 'Ocean Deep',
    description: 'Calming nautical marine blues with crisp cyan and electric aqua highlights',
    baseMode: 'dark',
    tokens: {
      ...DEFAULT_DARK_TOKENS,
      appBackground: '#05131E',
      primarySidebarBackground: '#081D2E',
      miniSidebarBackground: '#030D15',
      headerBackground: '#081D2E',
      sidebarText: '#F0F9FF',
      sidebarSecondaryText: '#7DD3FC',
      sidebarActiveBackground: '#0C3553',
      sidebarActiveText: '#38BDF8',
      sidebarHoverBackground: '#0A273E',
      sidebarBorder: '#0C3553',
      chatBackground: '#05131E',
      incomingMessageBackground: '#081D2E',
      outgoingMessageBackground: '#0C3553',
      composerBackground: '#081D2E',
      composerBorder: '#0F4870',
      primaryAccent: '#06B6D4',
      primaryButtonBackground: '#06B6D4',
      primaryButtonHover: '#0891B2',
      linkColor: '#22D3EE',
      focusRing: '#06B6D4',
      mentionBackground: '#0C3553',
      mentionText: '#67E8F9',
      mentionHoverBackground: '#155E75',
      cardBackground: '#081D2E',
      modalBackground: '#081D2E',
      dropdownBackground: '#081D2E',
      popoverBackground: '#081D2E',
      borderColor: '#0C3553',
    },
    previewColors: {
      sidebar: '#081D2E',
      chat: '#05131E',
      primary: '#06B6D4',
      card: '#081D2E',
    },
  },
  {
    id: 'lavender',
    name: 'Lavender Velvet',
    description: 'Delicate pastel lilac, violet gradients and royal plum for a soft creative aesthetic',
    baseMode: 'light',
    tokens: {
      ...DEFAULT_LIGHT_TOKENS,
      appBackground: '#FAF5FF',
      primarySidebarBackground: '#FFFFFF',
      miniSidebarBackground: '#F3E8FF',
      headerBackground: '#FFFFFF',
      sidebarText: '#3B0764',
      sidebarSecondaryText: '#7E22CE',
      sidebarActiveBackground: '#F3E8FF',
      sidebarActiveText: '#9333EA',
      sidebarHoverBackground: '#FAF5FF',
      sidebarBorder: '#E9D5FF',
      chatBackground: '#FAF5FF',
      incomingMessageBackground: '#FFFFFF',
      outgoingMessageBackground: '#F3E8FF',
      composerBackground: '#FFFFFF',
      composerBorder: '#D8B4FE',
      primaryAccent: '#9333EA',
      primaryButtonBackground: '#9333EA',
      primaryButtonHover: '#7E22CE',
      linkColor: '#9333EA',
      focusRing: '#9333EA',
      mentionBackground: '#F3E8FF',
      mentionText: '#7E22CE',
      mentionHoverBackground: '#E9D5FF',
      reactionActiveBackground: '#F3E8FF',
      reactionActiveBorder: '#C084FC',
      cardBackground: '#FFFFFF',
      borderColor: '#E9D5FF',
    },
    previewColors: {
      sidebar: '#F3E8FF',
      chat: '#FAF5FF',
      primary: '#9333EA',
      card: '#FFFFFF',
    },
  },
  {
    id: 'warm',
    name: 'Warm Espresso & Amber',
    description: 'Rich roasted espresso, terracotta amber, and cozy parchment warmth',
    baseMode: 'dark',
    tokens: {
      ...DEFAULT_DARK_TOKENS,
      appBackground: '#18120E',
      primarySidebarBackground: '#221914',
      miniSidebarBackground: '#120D0A',
      headerBackground: '#221914',
      sidebarText: '#FEF3C7',
      sidebarSecondaryText: '#D97706',
      sidebarActiveBackground: '#3D2A1F',
      sidebarActiveText: '#F59E0B',
      sidebarHoverBackground: '#2E221B',
      sidebarBorder: '#3D2A1F',
      chatBackground: '#18120E',
      incomingMessageBackground: '#221914',
      outgoingMessageBackground: '#3D2A1F',
      composerBackground: '#221914',
      composerBorder: '#523A2A',
      primaryAccent: '#D97706',
      primaryButtonBackground: '#D97706',
      primaryButtonHover: '#B45309',
      linkColor: '#FBBF24',
      focusRing: '#D97706',
      mentionBackground: '#3D2A1F',
      mentionText: '#FDE68A',
      mentionHoverBackground: '#78350F',
      cardBackground: '#221914',
      modalBackground: '#221914',
      dropdownBackground: '#221914',
      popoverBackground: '#221914',
      borderColor: '#3D2A1F',
    },
    previewColors: {
      sidebar: '#221914',
      chat: '#18120E',
      primary: '#D97706',
      card: '#221914',
    },
  },
  {
    id: 'highContrast',
    name: 'High Contrast OLED',
    description: 'Maximum accessibility pure black and white with vivid high-contrast neon markers',
    baseMode: 'dark',
    tokens: {
      ...DEFAULT_DARK_TOKENS,
      appBackground: '#000000',
      primarySidebarBackground: '#000000',
      miniSidebarBackground: '#000000',
      headerBackground: '#000000',
      sidebarText: '#FFFFFF',
      sidebarSecondaryText: '#E2E8F0',
      sidebarActiveBackground: '#262626',
      sidebarActiveText: '#FACC15',
      sidebarHoverBackground: '#171717',
      sidebarBorder: '#404040',
      chatBackground: '#000000',
      messageText: '#FFFFFF',
      messageSecondaryText: '#E2E8F0',
      messageHoverBackground: '#171717',
      incomingMessageBackground: '#0A0A0A',
      outgoingMessageBackground: '#1E1B4B',
      messageTimestamp: '#E2E8F0',
      threadBackground: '#000000',
      composerBackground: '#000000',
      composerBorder: '#737373',
      composerText: '#FFFFFF',
      composerPlaceholder: '#A3A3A3',
      primaryAccent: '#FACC15',
      primaryButtonBackground: '#FACC15',
      primaryButtonText: '#000000',
      primaryButtonHover: '#EAB308',
      secondaryButtonBackground: '#262626',
      secondaryButtonText: '#FFFFFF',
      secondaryButtonHover: '#404040',
      iconColor: '#FFFFFF',
      iconHoverColor: '#FACC15',
      linkColor: '#38BDF8',
      focusRing: '#FACC15',
      selectedState: '#262626',
      activeState: '#404040',
      mentionBackground: '#262626',
      mentionText: '#FACC15',
      mentionHoverBackground: '#404040',
      reactionBackground: '#171717',
      reactionActiveBackground: '#262626',
      reactionActiveBorder: '#FACC15',
      reactionBorder: '#525252',
      cardBackground: '#0A0A0A',
      modalBackground: '#000000',
      dropdownBackground: '#000000',
      popoverBackground: '#000000',
      tooltipBackground: '#000000',
      tooltipText: '#FFFFFF',
      borderColor: '#525252',
      dividerColor: '#525252',
      inputBackground: '#000000',
      inputBorder: '#737373',
      inputFocusBorder: '#FACC15',
      codeBlockBackground: '#000000',
      codeBlockText: '#FFFFFF',
      inlineCodeBackground: '#262626',
      inlineCodeText: '#FACC15',
    },
    previewColors: {
      sidebar: '#000000',
      chat: '#000000',
      primary: '#FACC15',
      card: '#0A0A0A',
    },
  },
];

export const THEME_CATEGORIES: ThemeCategoryConfig[] = [
  {
    key: 'shell',
    title: 'Application Shell',
    description: 'Main window frame, primary sidebar, workspace rail, and top header navigation',
    icon: 'Layout',
    fields: [
      {
        key: 'appBackground',
        label: 'Main App Background',
        description: 'Underlying background for the full window and layout boundaries',
      },
      {
        key: 'primarySidebarBackground',
        label: 'Primary Sidebar Background',
        description: 'Background color of the channel & navigation sidebar',
      },
      {
        key: 'miniSidebarBackground',
        label: 'Workspace Rail Background',
        description: 'Leftmost vertical rail hosting workspace icons and settings',
      },
      {
        key: 'headerBackground',
        label: 'Header / Top Bar Background',
        description: 'Top navigation bar across channels and direct messages',
      },
      {
        key: 'sidebarText',
        label: 'Sidebar Text Color',
        description: 'Main channel names, group headers, and workspace titles',
        contrastWith: 'primarySidebarBackground',
      },
      {
        key: 'sidebarSecondaryText',
        label: 'Sidebar Secondary Text',
        description: 'Muted counters, subtitle labels, and unread indicator tags',
        contrastWith: 'primarySidebarBackground',
      },
      {
        key: 'sidebarActiveBackground',
        label: 'Sidebar Active Item Background',
        description: 'Highlight pill behind currently selected channel or DM',
      },
      {
        key: 'sidebarActiveText',
        label: 'Sidebar Active Item Text',
        description: 'Color of selected channel or DM name',
        contrastWith: 'sidebarActiveBackground',
      },
      {
        key: 'sidebarHoverBackground',
        label: 'Sidebar Hover Background',
        description: 'Background color when hovering over channels and items',
      },
      {
        key: 'sidebarBorder',
        label: 'Sidebar Divider & Border',
        description: 'Border lines separating sidebars and header sections',
      },
    ],
  },
  {
    key: 'chat',
    title: 'Chat Workspace',
    description: 'Message stream, incoming & outgoing bubbles, message text, and composer input',
    icon: 'MessageSquare',
    fields: [
      {
        key: 'chatBackground',
        label: 'Chat Area Background',
        description: 'Main background behind message feeds and conversation lists',
      },
      {
        key: 'messageText',
        label: 'Message Text Color',
        description: 'Primary text color for all chat messages and descriptions',
        contrastWith: 'chatBackground',
      },
      {
        key: 'messageSecondaryText',
        label: 'Secondary Message Text',
        description: 'Sender names, timestamps, and edited badges',
        contrastWith: 'chatBackground',
      },
      {
        key: 'messageHoverBackground',
        label: 'Message Row Hover Background',
        description: 'Subtle background tint when hovering over a message row',
      },
      {
        key: 'incomingMessageBackground',
        label: 'Incoming Message Bubble',
        description: 'Background container for received messages',
      },
      {
        key: 'outgoingMessageBackground',
        label: 'Outgoing Message Bubble',
        description: 'Background container for messages sent by you',
      },
      {
        key: 'messageTimestamp',
        label: 'Message Timestamp',
        description: 'Date and time indicators next to sender names',
        contrastWith: 'chatBackground',
      },
      {
        key: 'threadBackground',
        label: 'Thread Drawer Background',
        description: 'Side drawer panel for viewing reply threads',
      },
      {
        key: 'composerBackground',
        label: 'Composer Background',
        description: 'Message input box and rich text editor background',
      },
      {
        key: 'composerBorder',
        label: 'Composer Border',
        description: 'Outer border and toolbar boundary around message input',
      },
      {
        key: 'composerText',
        label: 'Composer Text',
        description: 'Color of typed text inside the message composer',
        contrastWith: 'composerBackground',
      },
      {
        key: 'composerPlaceholder',
        label: 'Composer Placeholder',
        description: 'Placeholder prompt text when input is empty',
        contrastWith: 'composerBackground',
      },
    ],
  },
  {
    key: 'interactive',
    title: 'Interactive Elements',
    description: 'Buttons, links, action icons, focus rings, and selection indicators',
    icon: 'MousePointerClick',
    fields: [
      {
        key: 'primaryAccent',
        label: 'Primary Accent Color',
        description: 'Core brand and highlight accent used throughout the application',
      },
      {
        key: 'primaryButtonBackground',
        label: 'Primary Button Background',
        description: 'Main action buttons (e.g. Send, Create, Save)',
      },
      {
        key: 'primaryButtonText',
        label: 'Primary Button Text',
        description: 'Label text inside primary buttons',
        contrastWith: 'primaryButtonBackground',
      },
      {
        key: 'primaryButtonHover',
        label: 'Primary Button Hover',
        description: 'Hover state color for primary action buttons',
      },
      {
        key: 'secondaryButtonBackground',
        label: 'Secondary Button Background',
        description: 'Cancel, outline, and secondary action buttons',
      },
      {
        key: 'secondaryButtonText',
        label: 'Secondary Button Text',
        description: 'Text inside secondary action buttons',
        contrastWith: 'secondaryButtonBackground',
      },
      {
        key: 'secondaryButtonHover',
        label: 'Secondary Button Hover',
        description: 'Hover state for secondary buttons',
      },
      {
        key: 'iconColor',
        label: 'Standard Icon Color',
        description: 'Default color for navigation and tool icons',
      },
      {
        key: 'iconHoverColor',
        label: 'Icon Hover Color',
        description: 'Color of icons when hovered or active',
      },
      {
        key: 'linkColor',
        label: 'Link Color',
        description: 'Clickable links and URL mentions inside messages',
      },
      {
        key: 'focusRing',
        label: 'Focus Ring Color',
        description: 'Outline ring shown when focusing inputs and buttons',
      },
      {
        key: 'selectedState',
        label: 'Selected State',
        description: 'Background tint for selected items or checked options',
      },
      {
        key: 'activeState',
        label: 'Active / Pressed State',
        description: 'Color when clicking or holding down interactive controls',
      },
    ],
  },
  {
    key: 'mentionsReactions',
    title: 'Mentions & Reactions',
    description: 'User mentions, channel mentions, and emoji reaction badges',
    icon: 'AtSign',
    fields: [
      {
        key: 'mentionBackground',
        label: 'Mention Background',
        description: 'Pill background for @user and @channel tags',
      },
      {
        key: 'mentionText',
        label: 'Mention Text',
        description: 'Text color inside mention pills',
        contrastWith: 'mentionBackground',
      },
      {
        key: 'mentionHoverBackground',
        label: 'Mention Hover Background',
        description: 'Background when hovering over a mention pill',
      },
      {
        key: 'reactionBackground',
        label: 'Reaction Pill Background',
        description: 'Default background for emoji reaction badges',
      },
      {
        key: 'reactionActiveBackground',
        label: 'Active Reaction Background',
        description: 'Background for reactions added by you',
      },
      {
        key: 'reactionActiveBorder',
        label: 'Active Reaction Border',
        description: 'Border outline for your active reactions',
      },
      {
        key: 'reactionBorder',
        label: 'Reaction Border',
        description: 'Border outline for default reactions',
      },
    ],
  },
  {
    key: 'cardsOverlays',
    title: 'Cards, Panels & Overlays',
    description: 'Modals, popovers, dropdown menus, tooltips, and text inputs',
    icon: 'Layers',
    fields: [
      {
        key: 'cardBackground',
        label: 'Card Background',
        description: 'Background for dashboard cards, files list, and widgets',
      },
      {
        key: 'modalBackground',
        label: 'Modal Dialog Background',
        description: 'Background for dialog boxes and settings windows',
      },
      {
        key: 'dropdownBackground',
        label: 'Dropdown Menu Background',
        description: 'Context menus and action dropdown lists',
      },
      {
        key: 'popoverBackground',
        label: 'Popover Background',
        description: 'Emoji pickers, mention popovers, and status flyouts',
      },
      {
        key: 'tooltipBackground',
        label: 'Tooltip Background',
        description: 'Hover tooltip indicator bubbles',
      },
      {
        key: 'tooltipText',
        label: 'Tooltip Text',
        description: 'Text inside hover tooltips',
        contrastWith: 'tooltipBackground',
      },
      {
        key: 'borderColor',
        label: 'Standard Border Color',
        description: 'General card, panel, and separator border color',
      },
      {
        key: 'dividerColor',
        label: 'Divider Line Color',
        description: 'Horizontal divider lines between sections',
      },
      {
        key: 'inputBackground',
        label: 'Input Field Background',
        description: 'Text input boxes, search bars, and dropdown selectors',
      },
      {
        key: 'inputBorder',
        label: 'Input Field Border',
        description: 'Default border around input fields',
      },
      {
        key: 'inputFocusBorder',
        label: 'Input Focus Border',
        description: 'Border color when an input field is active and focused',
      },
    ],
  },
  {
    key: 'status',
    title: 'Status & System Colors',
    description: 'Presence status badges (online, away, offline) and alert tones',
    icon: 'Activity',
    fields: [
      {
        key: 'statusOnline',
        label: 'Online Status',
        description: 'Presence dot color for active team members',
      },
      {
        key: 'statusAway',
        label: 'Away / Idle Status',
        description: 'Presence dot color for idle or snoozed members',
      },
      {
        key: 'statusOffline',
        label: 'Offline Status',
        description: 'Presence dot color for offline members',
      },
      {
        key: 'success',
        label: 'Success Color',
        description: 'Success alerts, checkmarks, and positive confirmations',
      },
      {
        key: 'warning',
        label: 'Warning Color',
        description: 'Caution alerts, notices, and pending badges',
      },
      {
        key: 'error',
        label: 'Error / Destructive Color',
        description: 'Error messages, delete buttons, and danger alerts',
      },
      {
        key: 'info',
        label: 'Info Color',
        description: 'Informational tips, banner highlights, and system tags',
      },
    ],
  },
  {
    key: 'codeContent',
    title: 'Code & Developer Content',
    description: 'Code blocks, inline code tags, and syntax highlighting tones',
    icon: 'Code2',
    fields: [
      {
        key: 'codeBlockBackground',
        label: 'Code Block Background',
        description: 'Background container for multi-line code snippets',
      },
      {
        key: 'codeBlockText',
        label: 'Code Block Text',
        description: 'Default code snippet text',
        contrastWith: 'codeBlockBackground',
      },
      {
        key: 'inlineCodeBackground',
        label: 'Inline Code Background',
        description: 'Background for inline `code` tags inside messages',
      },
      {
        key: 'inlineCodeText',
        label: 'Inline Code Text',
        description: 'Text color for inline `code` tags',
        contrastWith: 'inlineCodeBackground',
      },
      {
        key: 'syntaxKeyword',
        label: 'Syntax Keyword',
        description: 'Keywords such as const, function, return, import',
        contrastWith: 'codeBlockBackground',
      },
      {
        key: 'syntaxString',
        label: 'Syntax String',
        description: 'Quoted string literals and regex values',
        contrastWith: 'codeBlockBackground',
      },
      {
        key: 'syntaxFunction',
        label: 'Syntax Function',
        description: 'Function names, classes, and method calls',
        contrastWith: 'codeBlockBackground',
      },
      {
        key: 'syntaxComment',
        label: 'Syntax Comment',
        description: 'Code comment lines and documentation blocks',
        contrastWith: 'codeBlockBackground',
      },
    ],
  },
];
